import { Link, useParams } from "react-router-dom";
import useSeo from "@/hooks/useSeo";
import ComicCard from "@/components/common/ComicCard";
import Breadcrumb from "@/components/common/Breadcrumb";
import { getCategoryBySlug, getComicById, getComicsByCategory } from "@/mocks/comics";

export default function ComicDetail() {
  const { id = "" } = useParams();
  const comic = getComicById(id);
  const category = comic ? getCategoryBySlug(comic.category) : undefined;
  const related = comic ? getComicsByCategory(comic.category).filter((c) => c.id !== comic.id) : [];

  useSeo({
    title: `${comic ? `${comic.title}漫画推荐 - ` : ""}高分漫画口碑推荐 | 我爱漫画网`,
    description: comic
      ? `${comic.title}（${comic.author}）${comic.reason} 来自我爱漫画网的${category?.name}推荐。`
      : "漫画推荐与补番清单。",
    path: `/comic/${id}`,
  });

  if (!comic || !category) {
    return (
      <div className="container-page flex flex-col items-center py-24 text-center">
        <h1 className="font-heading text-2xl font-bold text-foreground-950">作品不存在</h1>
        <p className="mt-3 text-sm text-foreground-600">这部作品可能已被调整，去看看推荐榜单吧。</p>
        <Link to="/rankings" className="mt-6 rounded-full bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600">
          去推荐榜单
        </Link>
      </div>
    );
  }

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: `${comic.title}漫画推荐`,
    description: comic.reason,
    author: { "@type": "Organization", name: "我爱漫画网" },
    about: comic.title,
    genre: comic.genres.join("、"),
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: comic.rating,
      bestRating: 10,
      ratingCount: 1200,
    },
  };

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }} />

      <Breadcrumb
        items={[
          { label: "首页", to: "/" },
          { label: category.name, to: `/category/${category.slug}` },
          { label: comic.title },
        ]}
      />

      <section className="container-page pb-12 md:pb-16">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[300px_1fr] md:gap-8">
          <div className="mx-auto w-full max-w-[260px] lg:max-w-none">
            <div className="relative aspect-[3/4] w-full overflow-hidden rounded-lg border border-background-200">
              <img src={comic.cover} alt={comic.coverAlt} className="h-full w-full object-cover object-top" />
            </div>
          </div>

          <article>
            <div className="flex flex-wrap items-center gap-2">
              <Link
                to={`/category/${category.slug}`}
                className="inline-flex items-center gap-1 rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-800 transition-colors hover:bg-secondary-200"
              >
                <i className={category.icon} />
                {category.name}
              </Link>
              {comic.tags.map((t) => (
                <span key={t} className="rounded-full bg-background-100 px-3 py-1 text-xs text-foreground-600">
                  {t}
                </span>
              ))}
            </div>

            <h1 className="mt-4 font-heading text-3xl font-bold text-foreground-950 md:text-4xl">{comic.title}</h1>
            <p className="mt-1.5 text-sm text-foreground-500">{comic.author} · {comic.year} · {comic.status}</p>

            <dl className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
              <div className="rounded-md bg-accent-100 px-4 py-3 text-center">
                <dt className="text-xs text-accent-700">评分</dt>
                <dd className="mt-0.5 font-heading text-xl font-bold text-accent-800">{comic.rating}</dd>
              </div>
              <div className="rounded-md bg-background-100 px-4 py-3 text-center">
                <dt className="text-xs text-foreground-500">连载状态</dt>
                <dd className="mt-0.5 text-sm font-bold text-foreground-800">{comic.status}</dd>
              </div>
              <div className="rounded-md bg-background-100 px-4 py-3 text-center">
                <dt className="text-xs text-foreground-500">首次连载</dt>
                <dd className="mt-0.5 text-sm font-bold text-foreground-800">{comic.year}</dd>
              </div>
              <div className="rounded-md bg-background-100 px-4 py-3 text-center">
                <dt className="text-xs text-foreground-500">作品类型</dt>
                <dd className="mt-0.5 text-sm font-bold text-foreground-800">{comic.genres.join(" · ")}</dd>
              </div>
            </dl>

            <h2 className="mt-8 font-heading text-lg font-bold text-foreground-950 md:text-xl">作品简介</h2>
            <p className="mt-3 leading-relaxed text-foreground-700">{comic.synopsis}</p>

            <div className="mt-6 rounded-lg border border-primary-200/70 bg-primary-50 p-5">
              <h2 className="flex items-center gap-2 font-heading text-base font-bold text-primary-700 md:text-lg">
                <i className="ri-star-fill text-accent-500" />
                编辑部推荐理由
              </h2>
              <p className="mt-2 leading-relaxed text-foreground-800">{comic.reason}</p>
            </div>

            <div className="mt-6 flex flex-col gap-3 sm:flex-row">
              <Link
                to="/rankings"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-list-check-3" />
                去逛更多榜单
              </Link>
              <Link
                to={`/category/${category.slug}`}
                className="inline-flex items-center justify-center gap-2 rounded-full border border-background-200 bg-background-100 px-6 py-3 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-300"
              >
                返回{category.name}
              </Link>
            </div>
          </article>
        </div>
      </section>

      {related.length > 0 && (
        <section className="bg-background-100">
          <div className="container-page py-10 md:py-12">
            <div className="mb-6 flex flex-col justify-between gap-3 sm:flex-row sm:items-end">
              <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                同栏目 · 更多推荐
              </h2>
              <Link to={`/category/${category.slug}`} className="inline-flex items-center gap-1 text-sm font-medium text-primary-600 hover:text-primary-700">
                查看全部{category.name}
                <i className="ri-arrow-right-line" />
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 md:gap-5">
              {related.map((c) => (
                <ComicCard key={c.id} comic={c} showCategory={false} />
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
}